import { useEffect, useMemo, useState } from "react";
import { whenFree } from "@/lib/popupQueue";
import { useNavigate } from "react-router-dom";
import { Sparkles, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { confetti, playFanfare } from "@/lib/celebrate";
import { VipMembershipCard } from "@/components/VipMembershipCard";

const GOLD = ["#fbcfe8", "#f472b6", "#e11d48", "#fde68a", "#f9a8c9", "#fff1f2"];

function GoldRain() {
  const bits = useMemo(() => Array.from({ length: 46 }, (_, i) => ({ l: Math.random() * 100, d: 2.6 + Math.random() * 2.8, delay: Math.random() * 3, s: 6 + Math.random() * 9, c: GOLD[i % GOLD.length], r: Math.random() > 0.5 })), []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {bits.map((b, i) => <i key={i} className="vip-fall absolute -top-6" style={{ left: `${b.l}%`, width: b.s, height: b.r ? b.s : b.s * 0.45, background: b.c, borderRadius: b.r ? "50%" : 2, animationDuration: `${b.d}s`, animationDelay: `${b.delay}s` }} />)}
    </div>
  );
}

export function VipCelebration() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const kind = user?.vip_celebrate;
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!kind) return undefined;
    whenFree(`vip-${kind}`, () => {
      setOpen(true);
      playFanfare();
      [0, 450, 900].forEach((d, i) => setTimeout(() => confetti({ x: innerWidth * [0.5, 0.25, 0.75][i], y: innerHeight * 0.35, count: 70, spread: 1.6, colors: GOLD }), d));
    });
    return undefined;
  }, [kind]);
  if (!kind || !open) return null;
  const close = (to) => {
    setOpen(false);
    setUser({ ...user, vip_celebrate: undefined });
    api.post("/vip/celebrated").catch(() => {});
    if (to) nav(to);
  };
  const isNew = kind === "new";
  const isDonation = kind === "donation";
  const isCard = kind === "card";
  const ct = user.card_thanks || {};
  return (
    <div role="dialog" aria-label="Unterstützer-Willkommen" className="fixed inset-0 z-[80] flex items-center justify-center p-4 vip-celebrate-bg backdrop-blur-md fade-up" data-testid="vip-celebration">
      <GoldRain />
      <div className="relative w-full max-w-md text-center space-y-5">
        <div className="relative mx-auto w-24 h-24 vip-crown-pop">
          <span className="absolute inset-0 rounded-full bg-pink-400/40 blur-2xl animate-pulse" />
          <span className="relative w-24 h-24 rounded-full bg-gradient-to-br from-pink-300 via-rose-500 to-amber-300 flex items-center justify-center shadow-2xl shadow-pink-500/40 ring-4 ring-pink-200/50"><Heart className="w-12 h-12 text-white" fill="currentColor" /></span>
          <Sparkles className="absolute -top-2 -right-3 w-7 h-7 text-pink-200 vip-twinkle" />
          <Sparkles className="absolute bottom-0 -left-4 w-5 h-5 text-amber-300 vip-twinkle" style={{ animationDelay: "0.6s" }} />
        </div>
        <div>
          <p className="text-[11px] font-extrabold tracking-[0.35em] vip-gold-text">{isDonation ? "DANKE FÜR DEINE SPENDE" : isCard ? "DANKE FÜR DEINE UNTERSTÜTZUNG" : isNew ? "WILLKOMMEN IM UNTERSTÜTZER-CLUB" : "UNTERSTÜTZUNG VERLÄNGERT"}</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white mt-2" data-testid="vip-celebration-title">{isDonation ? `Tausend Dank, ${user.name}! 💖` : isCard ? `Tausend Dank, ${user.name}! 💖` : isNew ? `Danke, ${user.name}! 💖` : `Schön, dass du bleibst, ${user.name}! 💖`}</h2>
          <p className="text-stone-300 text-sm mt-2" data-testid="vip-celebration-text">{isDonation ? `Deine Spende${user.donation_thanks_amount ? ` über ${user.donation_thanks_amount} €` : ""} ist angekommen und hilft uns riesig! Als Dankeschön bist du jetzt 7 Tage Projekt Unterstützer – mit leuchtendem Herz-Profil, rosé-goldenen Beiträgen und allen Unterstützer-Vorteilen.` : isCard ? `Deine Zahlung über ${ct.amount} € ist angekommen. Als Dankeschön bist du jetzt ${ct.days === 30 ? "1 Monat" : `${ct.days} Tage`} Projekt Unterstützer – mit leuchtendem Herz-Profil, rosé-goldenen Beiträgen und allen Unterstützer-Vorteilen.` : isNew ? "Du bist jetzt Projekt Unterstützer – mit leuchtendem Herz-Profil, rosé-goldenen Beiträgen, exklusiven Unterstützer-Geschenken und nach 30 Tagen am Stück deiner ersten Mystery-Box per Post." : "Deine Unterstützer-Mitgliedschaft wurde verlängert. Dein leuchtendes Herz-Profil und alle Unterstützer-Vorteile bleiben dir erhalten."}</p>
        </div>
        <div className="vip-card-flip text-left"><VipMembershipCard user={user} /></div>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <button onClick={() => close(`/u/${user.user_id}`)} data-testid="vip-celebration-profile" className="h-12 px-6 rounded-full bg-gradient-to-r from-pink-300 via-amber-200 to-amber-400 text-rose-950 font-extrabold shadow-lg shadow-amber-500/30 hover:brightness-105 active:scale-95 transition-[transform,filter]">Mein Unterstützer-Profil ansehen</button>
          <button onClick={() => close()} data-testid="vip-celebration-close" className="h-12 px-6 rounded-full border border-pink-300/50 text-pink-100 font-bold hover:bg-white/5 active:scale-95 transition-[transform,background-color]">Weiter</button>
        </div>
      </div>
    </div>
  );
}
